import React, { useEffect, useState } from 'react';
import Modal from '$/components/Modal';
import classNames from 'classnames';
import PagPlayer from '@/components/PagPlayer';

import styles from './index.module.scss';

export type ActivityPopupMediaType = 'image' | 'pag';

/** 活动弹窗默认宽高，接口未返回有效宽高时使用 */
const DEFAULT_POPUP_WIDTH = 624;
const DEFAULT_POPUP_HEIGHT = 468;

interface ActivityPopupInfoProps {
  open: boolean;
  image: string;
  /** PAG 加载失败时的静态图兜底地址 */
  fallbackImage?: string;
  mediaType?: ActivityPopupMediaType;
  /** 展示宽度（优先取 pag / img），无效时回落默认值 */
  width?: number;
  /** 展示高度（优先取 pag / img），无效时回落默认值 */
  height?: number;
  /** PAG 播放次数；undefined 表示无限循环。 */
  pagPlayCount?: number;
  closeBtnPosition?: 'topRight' | 'bottomCenter';
  maskClosable?: boolean;
  onClose: () => void;
  onImageClick: () => void;
  /** 无兜底图且 PAG 加载失败时回调 */
  onMediaError?: (error: unknown) => void;
}

/**
 * 将接口宽高规范为可用展示尺寸；0 / 非法值回落默认宽高。
 */
function resolvePopupSize(width?: number, height?: number) {
  return {
    width: width && width > 0 ? width : DEFAULT_POPUP_WIDTH,
    height: height && height > 0 ? height : DEFAULT_POPUP_HEIGHT,
  };
}

/**
 * 活动弹窗：支持图片与 PAG；PAG 失败时切换到 fallbackImage。
 */
export default function ActivityPopup({
  open,
  image,
  fallbackImage,
  mediaType = 'image',
  width,
  height,
  pagPlayCount,
  closeBtnPosition = 'bottomCenter',
  maskClosable = false,
  onClose,
  onImageClick,
  onMediaError,
}: ActivityPopupInfoProps) {
  // PAG 加载失败后切到静态兜底图，避免弹窗直接关闭。
  const [useFallback, setUseFallback] = useState(false);
  // PAG 文件较大时先展示 fallbackImage 首帧，待 PAG 真正可播放后再切换到动画。
  const [pagReady, setPagReady] = useState(false);

  useEffect(() => {
    setUseFallback(false);
    setPagReady(false);
  }, [image, open, mediaType]);

  const showPag = mediaType === 'pag' && !useFallback;
  const showPagFirstFrame = showPag && Boolean(fallbackImage) && !pagReady;
  const displayImage = useFallback && fallbackImage ? fallbackImage : image;
  // 容器尺寸始终以主素材宽高为准，切换兜底图时不抖动尺寸。
  const size = resolvePopupSize(width, height);

  /** PAG 加载失败：有兜底图则切换展示，否则上抛给调用方处理。 */
  const handlePagError = (error: unknown) => {
    if (fallbackImage) {
      setUseFallback(true);
      return;
    }
    onMediaError?.(error);
  };

  /** PAG 首帧可渲染后隐藏静态首帧占位，避免大文件加载完成前空白。 */
  const handlePagReady = () => {
    setPagReady(true);
  };

  return (
    <Modal
      open={open}
      width="auto"
      onCancel={onClose}
      wrapClassName={styles.ActivityPopupWrapper}
      bodyClassName={styles.ActivityPopup}
      maskStyle={{
        background: 'var(--background-backgroundMaskPrimary, rgba(15, 15, 15, 0.90))',
      }}
      destroyOnClose
      closable={false}
      maskClosable={maskClosable}
    >
      <div
        className={styles.content}
        style={{ width: size.width, height: size.height }}
      >
        {showPag ? (
          <>
            <PagPlayer
              url={image}
              className={styles.img}
              onClick={onImageClick}
              onError={handlePagError}
              onReady={handlePagReady}
              repeatCount={pagPlayCount}
            />
            {showPagFirstFrame && (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, @next/next/no-img-element
              <img
                className={classNames(styles.img, styles.pagFirstFrame)}
                src={fallbackImage}
                width={size.width}
                height={size.height}
                alt=""
                onClick={onImageClick}
              />
            )}
          </>
        ) : (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, @next/next/no-img-element
          <img
            className={styles.img}
            src={displayImage}
            width={size.width}
            height={size.height}
            alt=""
            onClick={onImageClick}
          />
        )}
        <div
          className={
            classNames(styles.closeBtn, closeBtnPosition === 'topRight' ? styles.closeBtnTopRight : styles.closeBtnBottomCenter)
            }
          onClick={onClose}
        />
      </div>
    </Modal>
  );
}
